// frontend/src/modules/reports/pages/financial/registry.ts
//
// Phase 4 (Reports Frontend). Financial-family report registry. OWNERSHIP:
// Task 4 owns this file exclusively (append entries + add the page files
// under pages/financial/*) — no other task edits this file or its pages.
//
// Seeded empty for Task 1 (the Financial tab correctly renders as "not built
// yet" until Task 4 lands). Gating matrix Task 4 must encode (mirrors the
// backend requireFamily() stacks in api/modules/reports/financial.routes.ts
// verbatim):
//   revenue / invoice-aging / cash-trend -> invoices.view + module 'invoicing'
//   expenses                             -> expenses.read + module 'expenses'
//   payroll-cost                         -> payroll.view + module 'payroll'
//   project-profitability                -> ALL OF projects.reports + invoices.view
//                                            + modules 'projects' + 'invoicing'
//   pnl-lite                             -> ALL OF invoices.view + expenses.read
//                                            + payroll.view + modules 'invoicing'
//                                            + 'expenses' + 'payroll'

import type { ReportRegistryEntry } from '../../registry';
import { RevenuePage } from './RevenuePage';
import { InvoiceAgingPage } from './InvoiceAgingPage';
import { ExpensesPage } from './ExpensesPage';
import { PayrollCostPage } from './PayrollCostPage';
import { ProjectProfitabilityPage } from './ProjectProfitabilityPage';
import { PnlLitePage } from './PnlLitePage';
import { CashTrendPage } from './CashTrendPage';

export const financialReportsRegistry: ReportRegistryEntry[] = [
  {
    key: 'revenue',
    title: 'Revenue',
    description: 'Billed vs. collected amounts over time, and by client.',
    permissions: ['invoices.view'],
    modules: ['invoicing'],
    component: RevenuePage,
  },
  {
    key: 'invoice-aging',
    title: 'Invoice aging',
    description: 'Outstanding invoice balances by age bucket, as of today.',
    permissions: ['invoices.view'],
    modules: ['invoicing'],
    component: InvoiceAgingPage,
  },
  {
    key: 'cash-trend',
    title: 'Cash trend',
    description: 'Billed vs. collected cash flow over time.',
    permissions: ['invoices.view'],
    modules: ['invoicing'],
    component: CashTrendPage,
  },
  {
    key: 'expenses',
    title: 'Expenses',
    description: 'Committed cost by category over time, and approval status.',
    permissions: ['expenses.read'],
    modules: ['expenses'],
    component: ExpensesPage,
  },
  {
    key: 'payroll-cost',
    title: 'Payroll cost',
    description: 'Gross, tax, and net payroll cost by run or by month.',
    permissions: ['payroll.view'],
    modules: ['payroll'],
    component: PayrollCostPage,
  },
  {
    key: 'project-profitability',
    title: 'Project profitability',
    description: 'Billable value, labor cost, and margin per project.',
    permissions: ['projects.reports', 'invoices.view'],
    modules: ['projects', 'invoicing'],
    requiresAllPermissions: true,
    component: ProjectProfitabilityPage,
  },
  {
    key: 'pnl-lite',
    title: 'P&L (lite)',
    description: 'Revenue, expenses, payroll, and net — a simplified profit & loss view.',
    permissions: ['invoices.view', 'expenses.read', 'payroll.view'],
    modules: ['invoicing', 'expenses', 'payroll'],
    requiresAllPermissions: true,
    component: PnlLitePage,
  },
];
